import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { PayCommissionPreviewStatus, RateType } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import {
  Control,
  FieldValues,
  Path,
  useController,
  UseControllerReturn,
} from 'react-hook-form';

import NumberInput from '~components/FormFields/v2/NumberInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { FeatureFlags } from '~constants/featureFlags';
import usePayCommissionPreview from '~hooks/usePayCommissionPreview/usePayCommissionPreview';
import { useStores } from '~store';
import { toDecimal } from '~utils/decimal';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  isLegacyCommissionRateType,
  isValidCommissionPercentage,
} from './commissionPercentageSchema';

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  grossRate: number | string | null | undefined;
  rateType: RateType | null | undefined;
  inheritedPercentage?: string | null;
  savedNetRate?: string | null;
  savedGrossRate?: number | string | null;
  savedRateType?: RateType | null;
  initialValue?: string | null;
  isDisabled?: boolean;
  isCreating?: boolean;
}

// Below the 400ms Doherty threshold, so the preview still feels immediate.
const PREVIEW_DEBOUNCE_MS = 300;

const percentageOrNull = <Value,>(value: Value) =>
  value === '' || isNil(value) ? null : value;

function PayCommissionControl<T extends FieldValues>(props: Props<T>) {
  const enabled = useFeatureFlag({ featureFlagKey: FeatureFlags.payCommissions });
  const { userStore } = useStores();
  const controller = useController({ control: props.control, name: props.name });
  const { onChange, value } = controller.field;
  const [mountedValue] = useState<string | null>(value);
  const initialValue =
    props.initialValue === undefined ? mountedValue : props.initialValue;
  const isLegacyCommission = isLegacyCommissionRateType(props.rateType);
  const savedPercentage = initialValue ?? null;
  const fieldPercentage = percentageOrNull(value);
  const hasLegacyDraft = isLegacyCommission && fieldPercentage !== savedPercentage;
  useEffect(() => {
    if (hasLegacyDraft) onChange(savedPercentage);
  }, [hasLegacyDraft, onChange, savedPercentage]);
  const hasSavedCommission = !isNil(initialValue) || !isNil(props.inheritedPercentage);
  if ((!enabled || isLegacyCommission) && !hasSavedCommission) return null;
  const permissions = userStore.getPermissions();
  if (!permissions.canViewRate) return null;
  return (
    <PayCommissionControlContent
      {...props}
      initialValue={initialValue}
      controller={controller}
      isDisabled={
        props.isDisabled ||
        isLegacyCommission ||
        !enabled ||
        !(props.isCreating ? permissions.canCreateRate : permissions.canEditRate)
      }
    />
  );
}

function PayCommissionControlContent<T extends FieldValues>({
  control,
  controller,
  name,
  grossRate,
  rateType,
  inheritedPercentage = null,
  savedNetRate,
  savedGrossRate,
  savedRateType,
  initialValue,
  isDisabled,
}: Props<T> & { controller: UseControllerReturn<T> }) {
  const { field } = controller;
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { preview } = usePayCommissionPreview();
  const isLegacyCommission = isLegacyCommissionRateType(rateType);
  const fieldPercentage = percentageOrNull(field.value);
  const isDraftChange = fieldPercentage !== (initialValue ?? null);
  const percentage = isLegacyCommission ? null : (fieldPercentage ?? inheritedPercentage);
  const [expanded, setExpanded] = useState(percentage !== null);
  const clearsInheritedCommission = isNil(initialValue) && inheritedPercentage !== null;
  const clearedInputPercentage = clearsInheritedCommission ? '0' : null;
  const [isInputCleared, setIsInputCleared] = useState(false);
  const displayedPercentage = isDraftChange ? field.value : percentage;
  const showsClearedInput = isLegacyCommission || (isInputCleared && isDraftChange);
  const removesSavedCommission =
    !isLegacyCommission &&
    !isNil(initialValue) &&
    fieldPercentage === null &&
    inheritedPercentage === null;
  const pendingRemovalHelperText = removesSavedCommission && (
    <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
      {t('pay_commission.removed_on_save')}
    </FormHelperText>
  );
  const [result, setResult] = useState<{
    key: string;
    rate: string | null;
    pending: boolean;
    error: boolean;
  } | null>(null);
  const inputKey = JSON.stringify([
    companyId,
    grossRate,
    rateType,
    percentage,
    savedNetRate,
  ]);
  const isValid =
    percentage !== null &&
    isValidCommissionPercentage(String(percentage)) &&
    grossRate !== null &&
    grossRate !== undefined &&
    /^-?\d+(\.\d+)?$/.test(String(grossRate)) &&
    Boolean(rateType);
  const hasCommission = expanded || percentage !== null;
  const matchesSaved =
    !isDraftChange && grossRate === savedGrossRate && rateType === savedRateType;

  useEffect(() => {
    if (!hasCommission || !isValid || !rateType) {
      setResult(null);
      return;
    }
    if (matchesSaved) {
      setResult(
        isNil(savedNetRate)
          ? null
          : { key: inputKey, rate: savedNetRate, pending: false, error: false },
      );
      return;
    }
    let active = true;
    const controller = new AbortController();
    setResult({ key: inputKey, rate: null, pending: true, error: false });
    const timer = setTimeout(async () => {
      try {
        const value = await preview(
          companyId,
          {
            gross_rate: String(grossRate),
            percentage: String(percentage),
            rate_type: rateType,
          },
          controller.signal,
        );
        if (active)
          setResult({
            key: inputKey,
            rate:
              value.status === PayCommissionPreviewStatus.CALCULATED
                ? value.netRate
                : null,
            pending: false,
            error: false,
          });
      } catch (error) {
        if (active && !axios.isCancel(error))
          setResult({ key: inputKey, rate: null, pending: false, error: true });
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [
    companyId,
    grossRate,
    hasCommission,
    inputKey,
    isValid,
    matchesSaved,
    percentage,
    preview,
    rateType,
    savedNetRate,
  ]);

  const current = result?.key === inputKey && isValid ? result : null;
  if (!hasCommission) {
    if (isDisabled) return null;
    return (
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <PseudoLink
          action={() => setExpanded(true)}
          dataTestId={`${name}-add`}
          linkColor={theme.semantic.linkText}
          title={t('pay_commission.add')}
          underline={false}
          variant="action"
        />
        {pendingRemovalHelperText}
      </Box>
    );
  }
  return (
    <Stack
      spacing={0.75}
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        flexBasis: '100%',
        mt: 0.5,
        px: 1.25,
        py: 1,
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <NumberInput
          control={control}
          controller={controller}
          displayValue={showsClearedInput ? '' : (displayedPercentage ?? '')}
          isDisabled={isDisabled}
          isInline
          label={
            <Typography variant="body2">
              {t('pay_commission.percentage_label')}
            </Typography>
          }
          name={name}
          onChange={(event) => {
            const isCleared = event.target.value === '';
            setIsInputCleared(isCleared);
            field.onChange(isCleared ? clearedInputPercentage : event.target.value);
          }}
          placeholder={percentage === null ? undefined : String(percentage)}
          slotProps={{
            htmlInput: {
              min: 0,
              max: 100,
              step: 'any',
              'aria-label': t('pay_commission.percentage_label'),
            },
            input: { sx: { width: 96 } },
          }}
        />
        {!isDisabled && percentage !== null && (
          <IconButton
            aria-label={t('pay_commission.remove')}
            onClick={() => {
              const removalIntent = isNil(field.value) ? '0' : null;
              setIsInputCleared(false);
              field.onChange(removalIntent);
              setExpanded(removalIntent !== null);
            }}
            size="small"
            sx={{
              color: 'text.secondary',
              ml: 'auto',
              p: 0.25,
              '&:hover': { color: 'error.main' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
      {pendingRemovalHelperText}
      {current?.pending && (
        <CircularProgress size={16} aria-label={t('pay_commission.preview_pending')} />
      )}
      {current?.error && (
        <Alert severity="error">{t('pay_commission.preview_failed')}</Alert>
      )}
      {current?.rate !== null && current?.rate !== undefined && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('pay_commission.actual_rate', {
            rate: toDecimal(current.rate).toFixed(
              Math.max(2, toDecimal(current.rate).decimalPlaces()),
            ),
          })}
        </Typography>
      )}
      {current && !current.pending && !current.error && current.rate === null && (
        <Typography variant="body2">{t('pay_commission.varies_by_payee')}</Typography>
      )}
    </Stack>
  );
}

export default observer(PayCommissionControl);
